import React, { ReactElement, PropsWithChildren, Suspense } from "react";
import { useRoutes, Navigate, type RouteObject } from "react-router-dom";

import AuthenticatedRoute from "./components/AuthenticatedRoute";
import UnauthenticatedRoute from "./components/UnauthenticatedRoute";
import { useAppContext } from "./lib/contextLib";
const NotFound = React.lazy(()=> import('./containers/NotFound'));

// Top Menu Section
const SubMenu = React.lazy(()=> import('./containers/SubMenu'));
const Home = React.lazy(()=> import('./containers/Home'));
const NotesList = React.lazy(()=> import('./containers/NotesList'));
const Login = React.lazy(()=> import('./containers/Login'));
const Signup = React.lazy(()=> import('./containers/Signup'));
const Donate = React.lazy(()=> import('./containers/Donate'));

// Notes Section
const Notes = React.lazy(()=> import('./containers/Notes/Notes'));
const NewNote = React.lazy(()=> import('./containers/Notes/NewNote'));

// Budget Section
const Overview = React.lazy(()=> import('./containers/Budget/Overview/Overview'));
const Items = React.lazy(()=> import('./containers/Budget/Items/Items'));
const Income = React.lazy(()=> import('./containers/Budget/Income/Income'));
const NewIncome = React.lazy(()=> import('./containers/Budget/Income/NewIncome'));
const Expense = React.lazy(()=> import('./containers/Budget/Expenses/Expenses'));
const NewExpense = React.lazy(()=> import('./containers/Budget/Expenses/NewExpense'));
const Savings = React.lazy(()=> import('./containers/Budget/Savings/Savings'));
const NewSaving = React.lazy(()=> import('./containers/Budget/Savings/NewSaving'));

// Portfolio Section
const Portfolio = React.lazy(()=> import('./containers/Portfolio/Overview/Overview'));
const Assets = React.lazy(()=> import('./containers/Portfolio/Assets/Assets'));
const NewAsset = React.lazy(()=> import('./containers/Portfolio/Assets/NewAsset'));
const Asset = React.lazy(()=> import('./containers/Portfolio/Assets/Asset/Asset'));
const Liabilities = React.lazy(()=> import('./containers/Portfolio/Liabilities/Liabilities'));
const NewLiability = React.lazy(()=> import('./containers/Portfolio/Liabilities/NewLiability'));
const Liability = React.lazy(()=> import('./containers/Portfolio/Liabilities/Liability/Liability'));
const Superannuation = React.lazy(()=> import('./containers/Portfolio/Superannuation/Superannuation'));
const NewSuperannuation = React.lazy(()=> import('./containers/Portfolio/Superannuation/NewSuperannuation'));
const SuperannuationDetail = React.lazy(()=> import('./containers/Portfolio/Superannuation/SuperannuationDetail'));

// Tools Sections
const PayCalculator = React.lazy(()=> import('./containers/Tools/PayCalculator/PayCalculator'));
const PayRates = React.lazy(()=> import('./containers/Tools/PayRates/PayRates'));
// Settings Page
const Settings = React.lazy(()=> import('./containers/Settings/Settings'));
// Dashboard
const Dashboard = React.lazy(()=> import('./containers/Dashboard/Dashboard'));

// Helper wrappers ensure children are grouped with SubMenu when authenticated.
// (We keep AuthenticatedRoute / UnauthenticatedRoute components unchanged.)
const WithSubMenu: React.FC<PropsWithChildren> = ({ children }) => (
  <>
    <SubMenu />
    <div className="content-width">{children}</div>
  </>
);

export default function Links() {
  const { isAuthenticated } = useAppContext();
  const wrapAuth = (el: ReactElement) => <AuthenticatedRoute>{el}</AuthenticatedRoute>;
  const wrapAuthWithMenu = (el: ReactElement) => wrapAuth(<WithSubMenu>{el}</WithSubMenu>);

  const Landing = () => isAuthenticated
    ? wrapAuthWithMenu(<Dashboard />)
    : <div className="content-width"><Home /></div>;

  const routes: RouteObject[] = [
  { path: '/', element: <Landing /> },
  { path: '/login', element: <UnauthenticatedRoute><div className="content-width"><Login /></div></UnauthenticatedRoute> },
  { path: '/signup', element: <UnauthenticatedRoute><div className="content-width"><Signup /></div></UnauthenticatedRoute> },
  { path: '/donate', element: wrapAuth(<div className="content-width"><Donate /></div>) },
    { path: '/notes/new', element: wrapAuth(<NewNote />) },
    { path: '/notes/:id', element: wrapAuth(<Notes />) },
    { path: '/budget', element: <Navigate to="/budget/overview" replace /> },
    { path: '/budget/overview', element: wrapAuthWithMenu(<Overview />) },
    { path: '/budget/items/:id', element: wrapAuthWithMenu(<Items />) },
    { path: '/budget/income', element: wrapAuthWithMenu(<Income />) },
    { path: '/budget/income/new', element: wrapAuthWithMenu(<NewIncome />) },
    { path: '/budget/expenses', element: wrapAuthWithMenu(<Expense />) },
    { path: '/budget/expenses/new', element: wrapAuthWithMenu(<NewExpense />) },
    { path: '/budget/savings', element: wrapAuthWithMenu(<Savings />) },
    { path: '/budget/savings/new', element: wrapAuthWithMenu(<NewSaving />) },
    { path: '/portfolio', element: <Navigate to="/portfolio/overview" replace /> },
    { path: '/portfolio/overview', element: wrapAuthWithMenu(<Portfolio />) },
    { path: '/portfolio/assets', element: wrapAuthWithMenu(<Assets />) },
    { path: '/portfolio/assets/new', element: wrapAuthWithMenu(<NewAsset />) },
    { path: '/portfolio/assets/:id', element: wrapAuthWithMenu(<Asset />) },
  { path: '/portfolio/superannuation', element: wrapAuthWithMenu(<Superannuation />) },
  { path: '/portfolio/superannuation/new', element: wrapAuthWithMenu(<NewSuperannuation />) },
  { path: '/portfolio/superannuation/:id', element: wrapAuthWithMenu(<SuperannuationDetail />) },
    { path: '/portfolio/liabilities', element: wrapAuthWithMenu(<Liabilities />) },
    { path: '/portfolio/liabilities/new', element: wrapAuthWithMenu(<NewLiability />) },
    { path: '/portfolio/liabilities/:id', element: wrapAuthWithMenu(<Liability />) },
  { path: '/notes/', element: wrapAuthWithMenu(<NotesList />) },
    { path: '/tools', element: <Navigate to="/tools/paycalculator" replace /> },
    { path: '/tools/paycalculator', element: wrapAuthWithMenu(<PayCalculator />) },
    { path: '/tools/payrates', element: wrapAuthWithMenu(<PayRates />) },
  { path: '/settings', element: wrapAuth(<div className="content-width"><Settings /></div>) },
    { path: '*', element: <NotFound /> }
  ];

  const element = useRoutes(routes);
  return (
    <Suspense fallback={<div style={{padding:'2rem', textAlign:'center', opacity:.6}}>Loading…</div>}>
  {element as React.ReactNode}
    </Suspense>
  );
}
